import { type ReactNode, useCallback, useEffect, useRef } from 'react';
import type { Location } from 'react-router-dom';
import { useBlocker, useNavigate } from 'react-router-dom';
import { useEncounterWorkflowGuard } from '@features/encounters';

import { ConfirmDialog } from '../primitives/ConfirmDialog';

interface EncounterNavigationGuardProps {
  children: ReactNode;
}

/**
 * Global navigation guard for encounter workflow protection
 *
 * Mounted at RootView level to intercept ALL navigation (navigate(), Link, browser back/forward)
 * across the entire authenticated app, even when navigating from non-encounter routes.
 *
 * Uses React Router's useBlocker with refs to prevent stale closures - the blocker callback
 * is created once but reads current values from refs, ensuring it always sees up-to-date
 * recording state even as the component re-renders.
 *
 * Enforces workflow protection rules:
 * - Allow navigation to completed/processing encounters (read-only, for reference)
 * - Block navigation to different pending encounters (would cause data loss/confusion)
 * - Show confirmation modal if blocked, stops recording if user confirms
 *
 * @example
 * User records on /encounters/A → navigates to /schedule → clicks appointment for /encounters/B
 * Guard intercepts the /schedule → /encounters/B navigation and shows confirmation modal
 */
export const EncounterNavigationGuard = ({ children }: EncounterNavigationGuardProps) => {
  const navigate = useNavigate();
  const {
    guardNavigation,
    isRecording,
    activeWorkflowEncounterId,
    showConfirmDialog,
    isProcessing,
    handleConfirmNavigation,
    handleCancelNavigation,
  } = useEncounterWorkflowGuard();

  // CRITICAL: Use refs to prevent stale closures
  // The blocker callback is created once with empty deps, so without refs it would
  // close over initial values. Refs let us read current values on every navigation attempt.
  const isRecordingRef = useRef(isRecording);
  const activeWorkflowEncounterIdRef = useRef(activeWorkflowEncounterId);

  // Update refs when values change
  useEffect(() => {
    isRecordingRef.current = isRecording;
    activeWorkflowEncounterIdRef.current = activeWorkflowEncounterId;
  }, [isRecording, activeWorkflowEncounterId]);

  // Determine if navigation should be blocked
  const shouldBlock = useCallback(({ currentLocation, nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
    // Read from refs to get current values (avoid stale closure)
    const currentIsRecording = isRecordingRef.current;
    const currentActiveWorkflowEncounterId = activeWorkflowEncounterIdRef.current;

    // Not recording - allow all navigation
    if (!currentIsRecording || !currentActiveWorkflowEncounterId) {
      return false;
    }

    // Navigating within same route - allow (e.g., React state changes)
    if (currentLocation.pathname === nextLocation.pathname) {
      return false;
    }

    // Check if target is an encounter route (includes /encounters and /first-encounter)
    const encounterRoutePattern = /^\/(encounters|first-encounter)(\/([a-f0-9-]+|active))?$/;
    const isTargetEncounterRoute = encounterRoutePattern.test(nextLocation.pathname);

    // Not navigating to encounter route - allow
    if (!isTargetEncounterRoute) {
      return false;
    }

    // Extract target encounter ID (group 3 captures the UUID or 'active')
    const match = nextLocation.pathname.match(encounterRoutePattern);
    const targetEncounterId = match?.[3] === 'active' ? null : match?.[3] ?? null;

    // Navigating to same encounter - allow
    if (targetEncounterId === currentActiveWorkflowEncounterId) {
      return false;
    }

    // Navigating to /encounters/active or /first-encounter (no ID) - allow
    if (!targetEncounterId) {
      return false;
    }

    // Block navigation to different encounter - will trigger guard check
    return true;
  }, []); // Empty deps - function uses refs so doesn't need to recreate

  const blocker = useBlocker(shouldBlock);

  // Handle blocked navigation
  useEffect(() => {
    const handleBlockedNavigation = async () => {
      if (blocker.state === 'blocked') {
        try {
          // Store target path for programmatic navigation
          const targetPath = blocker.location.pathname;

          // Use guard to check encounter status and show modal if needed
          // Returns false if modal shown, true if navigation allowed
          const allowed = await guardNavigation(targetPath, () => {
            // onConfirm callback - navigate programmatically after saving recording
            // This avoids blocker state transition errors (can't proceed on unblocked blocker)
            navigate(targetPath);
          });

          if (allowed) {
            // Navigation allowed (e.g., completed encounter) - proceed immediately
            blocker.proceed();
          } else {
            // Modal shown - reset blocker immediately
            // When user confirms, modal will navigate programmatically (no blocker involved)
            blocker.reset();
          }
        } catch (error) {
          console.error('[EncounterNavigationGuard] Error handling blocked navigation:', error);
          // On error, stay on current page
          blocker.reset();
        }
      }
    };

    handleBlockedNavigation();
  }, [blocker, guardNavigation, navigate]);

  return (
    <>
      {children}
      <ConfirmDialog
        open={showConfirmDialog}
        variant="warning"
        title="Recording In Progress"
        message={
          <>
            You are currently recording. Navigating to a different encounter will stop your current
            recording session.
            <br />
            <br />
            Your work will be saved, but the recording will end.
            <br />
            <br />
            Do you want to stop recording and navigate?
          </>
        }
        confirmText="Stop & Navigate"
        cancelText="Cancel"
        onConfirm={handleConfirmNavigation}
        onClose={handleCancelNavigation}
        loading={isProcessing}
      />
    </>
  );
};
